import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import NewSelectMultiSelect from '../../../../Components/PIM/common/NewSelectMultiSelect/NewSelectMultiSelect';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import ReadonlyTextCell from '../../../../Components/BombiixAGGrid/cells/ReadonlyTextCell';
import SelectInputCell from '../../../../Components/BombiixAGGrid/cells/SelectInputCell';
import type { BombiixCellRendererParams, BombiixRowId } from '../../../../Components/BombiixAGGrid';
import { RefEntity, TableRow } from './types';
import closeMenuOnAnchorScroll from '../../../../Components/BombiixAGGrid/cells/closeMenuOnAnchorScroll';

type ToggleCellProps = BombiixCellRendererParams<TableRow> & {
  options?: RefEntity[];
  toggleField: string;
  isInlineRow?: (data: TableRow) => boolean;
  placeholder?: string;
  /** Renders the paired control as a multi-select (delegated to SelectInputCell) rather than a single select. */
  isMulti?: boolean;
  /** Hover tooltip and accessible name for the switch. */
  toggleLabel?: string;
  /** Renders an info icon beside the switch; hovering the pair shows `toggleLabel`. */
  toggleInfo?: boolean;
  /** Whether flipping the switch off also clears the paired selection. */
  clearSelectionOnOff?: boolean;
  /** Whether flipping the switch off also disables the paired selection. */
  disableSelectionOnOff?: boolean;
};

/**
 * A switch paired with a select inside one grid cell, rendered on the inline add/edit row only
 * and saved with the row. List rows show the plain read-only text.
 *
 * Licensor (`isLicensedProduct` + licensor dropdown) is the original: the switch gates the dropdown
 * and clears it when turned off. Supplier(s) (`visibleToSupplier` + the supplier multi-select) opts
 * into `isMulti`, an info icon with the "Visible to Supplier" tooltip, and turns BOTH
 * `clearSelectionOnOff` and `disableSelectionOnOff` off (BM-9627 follow-up): visibility governs who
 * may see the product, not who may be assigned to it, so suppliers stay editable and are never
 * dropped while the switch is off. The Create / Edit Product page treats the same pair that way.
 *
 * Local `on` / `selectedId` state mirrors `data` because AG Grid skips refreshing the focused cell
 * to preserve the caret, which would otherwise leave the switch stale until the row re-renders.
 */
function ToggleCell(params: ToggleCellProps) {
  const {
    data,
    colDef,
    options = [],
    toggleField,
    isInlineRow,
    onRowChange,
    getRowId,
    error,
    placeholder,
    disabled,
    isMulti = false,
    toggleLabel,
    toggleInfo = false,
    clearSelectionOnOff = true,
    disableSelectionOnOff = true,
  } = params;
  const field = colDef?.field;
  const editable = data && isInlineRow ? isInlineRow(data) : false;
  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : data?._id;

  const [on, setOn] = useState<boolean>(Boolean(data?.[toggleField]));
  const [selectedId, setSelectedId] = useState<string>(field ? String(data?.[field] ?? '') : '');
  const selectWrapRef = useRef<HTMLDivElement | null>(null);
  const menuOpenRef = useRef(false);
  const [, setMenuPlacementTick] = useState(0);

  useEffect(() => {
    setOn(Boolean(data?.[toggleField]));
  }, [data, toggleField]);

  useEffect(() => {
    setSelectedId(field ? String(data?.[field] ?? '') : '');
  }, [data, field]);

  /* The nested grid cells are typed on the generic BombiixRow, whose `_id` is optional, which makes
     their `getRowId` contravariantly incompatible with this cell's TableRow-typed params. Every row
     this grid renders is a TableRow, so forwarding them is sound. */
  const nestedParams = params as unknown as BombiixCellRendererParams;

  if (!editable) return <ReadonlyTextCell {...nestedParams} />;

  const selectionDisabled = Boolean(disabled) || (disableSelectionOnOff && !on);

  const handleToggle = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setOn(checked);
    if (!checked && clearSelectionOnOff) setSelectedId('');
    event.target.blur();
    if (rowId !== undefined) onRowChange?.(rowId, toggleField, checked);
  };

  const handleSelect = (option: RefEntity | null) => {
    const value = String(option?._id ?? '');
    setSelectedId(value);
    if (rowId !== undefined && field) onRowChange?.(rowId, field, value);
  };

  const switchId = `ptv-toggle-${toggleField}-${String(rowId ?? 'row')}`;

  /* The native checkbox picks up the .design-switch slider styling from common.css, matching the
     Create Product page's toggles. */
  const switchControl = (
    <div className="ptv-toggle-cell__control">
      <div className="design-switch d-inline-flex align-items-center">
        <input
          type="checkbox"
          className="form-check-input mt-0 comment-switch-input"
          id={switchId}
          name={toggleField}
          checked={on}
          onChange={handleToggle}
          disabled={Boolean(disabled)}
          aria-label={toggleLabel ?? 'Licensed'}
        />
      </div>
      {toggleInfo ? <span className="icon-info ptv-toggle-cell__info" aria-hidden="true" /> : null}
    </div>
  );

  const toggle = toggleLabel ? (
    <CustomTooltip placement="top" tooltipText={toggleLabel}>
      {switchControl}
    </CustomTooltip>
  ) : (
    switchControl
  );

  /* The multi-select path reuses the grid's own select cell rather than restating its chip
     layout, scroll-preservation and menu-portal handling. */
  if (isMulti) {
    return (
      <div className="ptv-toggle-cell">
        {toggle}
        <div className="ptv-toggle-cell__select">
          <SelectInputCell {...nestedParams} disabled={selectionDisabled} />
        </div>
      </div>
    );
  }

  const selected = options.find((option) => String(option._id) === selectedId) || null;

  /* AG Grid cells have overflow: hidden — portal the menu to <body> so it isn't clipped and
     spilling into the rows below (same pattern as SelectInputCell). menuPortalTarget /
     menuShouldBlockScroll are forwarded verbatim by NewSelectMultiSelect's ...rest spread but
     aren't on its Props interface, so pass them through an untyped bag. */
  const portalProps: Record<string, unknown> = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuShouldBlockScroll: true,
    closeMenuOnScroll: closeMenuOnAnchorScroll({
      anchor: selectWrapRef,
      isOpen: () => menuOpenRef.current,
      onFollow: () => setMenuPlacementTick((tick) => tick + 1),
    }),
  };

  return (
    <div className={`ptv-toggle-cell${error ? ' cag-cell-wrap--error' : ''}`}>
      {toggle}
      <div ref={selectWrapRef} className="ptv-toggle-cell__select">
        <NewSelectMultiSelect<RefEntity>
          options={options}
          value={selected}
          getOptionLabel={(option: RefEntity) => option.name ?? ''}
          getOptionValue={(option: RefEntity) => String(option._id)}
          onChange={(value) => handleSelect((value as RefEntity | null) ?? null)}
          placeholder={placeholder ?? 'Select Licensor'}
          isDisabled={selectionDisabled}
          isClearable
          menuPosition="fixed"
          menuPlacement="auto"
          onMenuOpen={() => {
            menuOpenRef.current = true;
          }}
          onMenuClose={() => {
            menuOpenRef.current = false;
          }}
          {...portalProps}
        />
        {error ? <span className="cag-cell-error">{error}</span> : null}
      </div>
    </div>
  );
}

export default ToggleCell;
